import { z } from 'zod';
import { IsoDateTime } from './common.js';

/** How long location history is kept. Each device's latest location is always kept. */
export const RETENTION_CHOICES = [7, 14, 30, 90, 365] as const;
export const RetentionDays = z.union(RETENTION_CHOICES.map((n) => z.literal(n)));
export type RetentionDays = z.infer<typeof RetentionDays>;

/** The security activity log is kept this long, whatever the retention setting. */
export const ACTIVITY_RETENTION_DAYS = 90;

export const LostContact = z.string().trim().max(120);

export const Settings = z.object({
  retentionDays: z.number().int(),
  reverseGeocodeEnabled: z.boolean(),
  /** False when the server has address lookups switched off for everyone. */
  reverseGeocodeAvailable: z.boolean(),
  /** Pre-filled in the lost-mode form. Empty when not set. */
  defaultLostContact: z.string(),
  twoFactorEnabled: z.boolean(),
  /** Unused recovery codes; 0 when two-factor login is off. */
  recoveryCodesLeft: z.number().int(),
});
export type Settings = z.infer<typeof Settings>;

export const UpdateSettingsRequest = z
  .object({
    retentionDays: RetentionDays,
    reverseGeocodeEnabled: z.boolean(),
    defaultLostContact: LostContact,
  })
  .partial()
  .refine((body) => Object.keys(body).length > 0, 'Nothing to change.');
export type UpdateSettingsRequest = z.input<typeof UpdateSettingsRequest>;

/** Deleting history or the account needs the password typed again. */
export const ConfirmPasswordRequest = z.object({ password: z.string().min(1).max(1024) });
export type ConfirmPasswordRequest = z.input<typeof ConfirmPasswordRequest>;

export const DeleteHistoryResponse = z.object({ deleted: z.number().int() });
export type DeleteHistoryResponse = z.infer<typeof DeleteHistoryResponse>;

/** Settings → Security activity. */
export const ActivityEvent = z.object({
  id: z.string(),
  /** login_ok, login_failed, device_paired, export, … (new types may be added). */
  type: z.string(),
  ip: z.string().nullable(),
  createdAt: IsoDateTime,
  detail: z.record(z.string(), z.string()),
});
export type ActivityEvent = z.infer<typeof ActivityEvent>;

export const ActivityResponse = z.object({ events: z.array(ActivityEvent) });
export type ActivityResponse = z.infer<typeof ActivityResponse>;
